import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const DominicanRepublicPage = () => {
  const dominicanRepublicData = {
    name: 'Dominican Republic',
    region: 'Latin America',
    color: '#C85A54',
    metaTitle: 'Pharma & Nutraceutical Registration in Dominican Republic | DIGEMAPS | Regulatory Overview',
    metaDescription: 'Discover how to register pharmaceuticals and nutraceuticals in the Dominican Republic. DIGEMAPS requirements, sanitary registration routes, supplement regulation, documentation and why this Caribbean market deserves attention.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in the Dominican Republic',
      subtitle: 'An evolving Caribbean market with rising healthcare demand and modernising regulatory framework via DIGEMAPS - an opportunity for exporters who align with local standards and classification.',
      cta: 'Start Your Dominican Rep. Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~11.3 million (2024 estimate)' },
      { label: 'Regulator', value: 'DIGEMAPS - General Directorate of Medicines, Food & Health Products under Ministry of Public Health & Social Assistance' },
      { label: 'Key Trade Standard', value: 'Products such as pharmaceuticals, processed foods, cosmetics and dairy must receive certification of approval (Registro Sanitario) prior to market entry' },
      { label: 'Regulatory Modernisation', value: 'A 2021 Decree (284-21) reformed health-product registration and authorisation processes under DIGEMAPS' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Caribbean Gateway + Regulatory Update',
      stats: [
        'Pharmaceuticals, food-supplements and health-products must gain "registro sanitario" under the Ministry of Public Health\'s health control mandate',
        'Registration of pharmaceuticals requires a local distributor or branch, free-sale certificate, GMP certificate and other technical dossiers',
        'For food supplements, the Dominican Republic is currently consulting a "Proposed Regulation for Food Supplements" (August 2025) to create a unified framework',
        'The country has signed MoUs for regulatory-cooperation (e.g., with India\'s CDSCO) to jointly combat sub-standard medicines and support regulatory convergence'
      ],
      opportunities: [
        'Clear classification and correct local representation are vital',
        'Although a smaller scale market, successful entry can provide a Caribbean/Carib-Latin foothold',
        'The evolving supplement regulatory regime means early movers can gain advantage by aligning ahead of changes',
        'Particularly suitable for wellness/nutraceutical products'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Lifestyle & Import Dependence',
      subtitle: 'Caribbean and Latin-American region data show rising NCD burdens, with the Dominican Republic highlighting increased incidence of diabetes, hypertension and obesity.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            'Increased incidence of diabetes across population',
            'Rising hypertension prevalence',
            'Growing obesity rates',
            'Lifestyle disease trends common to Caribbean region',
            'Pharma demand: Antidiabetics, antihypertensives, lipid-lowering medicines',
            'Pharma demand: CVD therapies, chronic disease management',
            'Market demand for chronic-care therapies is growing'
          ]
        },
        {
          name: 'Wellness & Preventive Health',
          items: [
            'Wellness, dietary-supplement and preventive-health demand is increasing',
            'Middle-income consumers seek lifestyle products and imported brands',
            'Growing awareness of nutritional health',
            'Import-dependent market for quality health products',
            'Nutra opportunities: Metabolic health supplements',
            'Nutra opportunities: Cardiovascular support, vitamins/minerals',
            'Credible supplements addressing metabolic health in high demand'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'DIGEMAPS (General Directorate of Medicines, Foods and Sanitary Products)',
      description: 'DIGEMAPS under the Ministry of Public Health & Social Assistance, created via Art 109 of the General Health Law and Decree 284-21 (2021). Pharmaceutical, food, cosmetic and health-products must obtain a Registro Sanitario to manufacture, package, import or sell within the Dominican Republic. Standards-system through Instituto Dominicano para la Calidad (INDOCAL) / SIDOCAL coordinates national quality, accreditation and technical regulations.',
      pharma: {
        timeline: 'While explicit current timelines are not widely published, the reform (Decree 284-21) aims at "simplification of processes" under the health-modernisation agenda. Exporters should plan for several months for standard registrations, with more time needed if dossier or classification issues emerge.',
        requirements: [
          'A local company or authorised distributor must be registered with the health authority before submission',
          'Though full CTD adoption may not be mandatory everywhere, LatAm-guidance indicates finished product samples or at least product photos are required in registration dossiers',
          'Power of attorney from manufacturer to distributor',
          'Free Sale Certificate issued by official authority in country of origin (consular legalisation required)',
          'Label in Spanish (legend: "Keep out of reach of children")',
          'Qualitative/quantitative formula, manufacturing process, analysis certificates for finished product and raw materials',
          'Samples: six samples of finished product with three years validity may be requested',
          'Registrations ("Registro Sanitario") must be renewed periodically under DIGEMAPS rules',
          'Variations (formulation, site, supplier) must follow amendment procedures'
        ],
        insight: 'Decree 284-21 reformed registration processes with aim of simplification. Key requirements remain: local representation, free sale certificate with consular legalisation, Spanish labeling, complete technical dossiers and product samples. The modernisation agenda suggests improving timelines but exporters should still plan conservatively.'
      },
      nutra: {
        timeline: 'Varies based on product classification and documentation completeness. Faster than pharmaceutical route if correctly classified as food supplement. The new regulation framework (2025 consultation) will provide clearer timelines once enacted.',
        requirements: [
          'A formal regulation for food supplements is currently in public consultation (2025) and aims to create a unified framework for dosage forms, nutrients, botanicals, additive limits and labeling',
          'Until full regulation is enacted, such products may be treated under food import/sanitary registration or require pharmaceutical-type registration depending on claims, dosage form or composition',
          'Product classification (food vs medicine) must be carefully determined',
          'Compliant labeling in Spanish required',
          'Import representation and local distributor license mandatory',
          'Composition within safe/normative limits',
          'Free sale certificate from origin country',
          'Claims must not imply disease treatment unless registered as medicine',
          'Early-mover advantage for nutraceuticals aligned with forthcoming rules'
        ],
        insight: 'The draft food supplement regulation (August 2025) represents a significant opportunity for early movers. Exporters who align formulations, labeling and claims proactively with the emerging framework can gain first-mover advantage in this growing Caribbean wellness market.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration in the Dominican Republic',
      steps: [
        'Local Agent / MAH - A local company or authorised distributor must be registered with the health authority before submission',
        'Dossier Format - LatAm Norms (CTD-aligned) - finished product samples or at least product photos required',
        'Power of Attorney - From manufacturer to distributor',
        'Free Sale Certificate - Issued by official authority in country of origin (consular legalisation required)',
        'Label in Spanish - Must include legend: "Keep out of reach of children" ("Manténgase fuera del alcance de los niños")',
        'Qualitative/Quantitative Formula - Complete formulation details',
        'Manufacturing Process - Detailed manufacturing method description',
        'Analysis Certificates - For finished product and raw materials',
        'GMP Certificate - Of manufacturer/conditioner',
        'Product Samples - Six samples of finished product with three years validity may be requested',
        'Assessment & Timeline - Plan for several months for standard registrations under reformed process',
        'Renewal & Variations - Registrations must be renewed periodically; variations must follow amendment procedures'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals, Food Supplements & Health Products in the Dominican Republic',
      steps: [
        'Regulatory Status - A formal regulation for food supplements is currently in public consultation (2025)',
        'Framework Creation - Aims to create unified framework for dosage forms, nutrients, botanicals, additive limits and labeling',
        'Current Treatment - Until full regulation is enacted, products may be treated under food import/sanitary registration or pharmaceutical-type registration',
        'Classification Critical - Depends on claims, dosage form or composition',
        'Early-Mover Advantage - For nutraceuticals aligned with forthcoming rules',
        'Product Classification - Ensure correct classification (food vs medicine)',
        'Spanish Labeling - Compliant labeling required',
        'Local Representation - Import representation and local distributor license mandatory',
        'Composition Compliance - Within safe/normative limits',
        'Free Sale Certificate - From origin country required',
        'Claims Limitations - Must not imply disease treatment unless registered as medicine',
        'Align with Future Regulation - Position products for compliance with 2025 supplement framework'
      ]
    },
    
    requiredDocs: {
      'Pharmaceutical Products': [
        'Distributor power of attorney',
        'Free Sale Certificate from country of origin (legalised)',
        'GMP certificate of manufacturer/conditioner',
        'Label artwork in Spanish ("Manténgase fuera del alcance de los niños" legend)',
        'Qualitative/quantitative formulation',
        'Method of analysis, manuscript of manufacturing process',
        'Certificate of analysis for finished product and raw materials',
        'Six samples of finished product (in certain cases) with 3-year validity'
      ],
      'Nutraceuticals / Food Supplement Products': [
        'Composition and dosage details',
        'Label artwork in Spanish, use instructions, warnings',
        'Manufacturing GMP / quality certificate',
        'Free Sale Certificate from origin country',
        'Local importer/distributor licence or representation',
        'For imported consumables: sanitary import permit or registration',
        'Compliance with future regulation (nutrient limits, botanical listing, claims) per draft 2025 regulation'
      ]
    },
    
    whyThisCountry: {
      title: 'Why the Dominican Republic Belongs in Your Caribbean/LatAm Strategy',
      reasons: [
        'Emerging market with regulatory modernisation (Decree 284-21, new food supplement regulation in draft) - less saturated than some larger LatAm markets',
        'English-speaking exporters can use this market as a gateway to the Caribbean and Central American region',
        'Wellness/nutraceutical segment has room for growth with rising income and health-awareness',
        'Relatively fewer complex local dossier requirements compared with very large markets, allowing faster entry for well-prepared exporters',
        'Early-mover advantage available in evolving supplement regulatory landscape',
        'Strategic Caribbean/Latin American positioning for regional expansion'
      ],
      bestPortfolio: [
        'Pharma: Generics for NCDs (diabetes, hypertension), imported finished medicines from recognised regulatory jurisdictions, chronic-care therapies',
        'Nutra: Nutritional supplements for metabolic health, cardiovascular support, vitamins/minerals and premium wellness formats aligned with emerging regulatory clarity'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter the Dominican Republic',
      services: [
        'Portfolio Prioritisation - Identify which SKUs are best placed for Caribbean/LatAm channel, classification risk, local market fit and regulatory simplicity',
        'Dossier & Representation Setup - Adapt existing dossiers for Dominican Republic submission (localised label, Spanish documentation, free sale certificate, sample requirements) and select qualified local distributor/agent',
        'Supplement Strategy - Position your nutraceuticals in line with the forthcoming food-supplement regulation, adjust formulations/claims proactively and prepare for import-licence/registration procedures',
        'Import & Customs Compliance - Guide import-registration flow, sanitary permit process, local trade approvals and distributor supply-chain set-up',
        'LatAm/Caribbean Expansion - Leverage Dominican Republic entry to build regional scale - central Caribbean hub facilitating expansion into Puerto Rico, Central America and other Spanish-speaking Caribbean states'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and health products in the Dominican Republic?',
        answer: 'Registration is managed by the General Directorate of Medicines, Foods & Sanitary Products (DIGEMAPS) under the Ministry of Public Health & Social Assistance.'
      },
      {
        question: 'Are food supplements regulated differently from medicines?',
        answer: 'Yes. While no specific regulation was fully in force until 2025, a draft regulation for "Food Supplements" is under public consultation which will set specific nutrient/botanical limits and dosage-form rules.'
      },
      {
        question: 'What are the key documents for pharmaceutical registration?',
        answer: 'Distributors\' power of attorney, free sale certificate, GMP certificate, label in Spanish, formulations, analysis certificates, samples (in many cases) etc.'
      },
      {
        question: 'How long does registration take?',
        answer: 'Specific timelines are not always published; however, reforms (Decree 284-21) aim to simplify and accelerate the process. Planning for several months is prudent.'
      },
      {
        question: 'Can you register nutraceuticals ahead of full regulations?',
        answer: 'Yes - but you must watch classification risk, ensure formulation and claims are low-risk, prepare for imminent regulatory change and engage local representation/import licence process early.'
      }
    ]
  };

  return <CountryTemplate countryData={dominicanRepublicData} />;
};
